import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { TrendingUp, ArrowLeft, Loader2, Globe, Mail, KeyRound, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { t, toggleLanguage, language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Enter email/phone, 2: Enter code, 3: New password, 4: Success
  const [identifier, setIdentifier] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!identifier) {
      toast.error(t('Please enter your email or phone', 'Tafadhali ingiza barua pepe au nambari ya simu'));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { identifier });
      toast.success(t('If an account exists, a reset code has been sent.',
                      'Ikiwa akaunti ipo, msimbo wa kubadilisha umetumwa.'));
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to send code', 'Imeshindwa kutuma msimbo'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      toast.error(t('Please enter the 6-digit code', 'Tafadhali ingiza msimbo wa tarakimu 6'));
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/verify-reset-code?identifier=${encodeURIComponent(identifier)}&code=${encodeURIComponent(code)}`
      );
      // Backend returns the opaque token only AFTER the code is verified
      setResetToken(response.data.token);
      toast.success(t('Code verified!', 'Msimbo umethibitishwa!'));
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Invalid or expired code', 'Msimbo si sahihi au umeisha muda'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    // Server-side complexity is enforced by backend. Mirror its rules here for instant UX feedback.
    const complexityIssue =
      newPassword.length < 8 ? t('Password must be at least 8 characters', 'Nenosiri lazima liwe na herufi 8 au zaidi') :
      !/[A-Z]/.test(newPassword) ? t('Password must contain an uppercase letter', 'Nenosiri lazima liwe na herufi kubwa') :
      !/[a-z]/.test(newPassword) ? t('Password must contain a lowercase letter', 'Nenosiri lazima liwe na herufi ndogo') :
      !/[0-9]/.test(newPassword) ? t('Password must contain a number', 'Nenosiri lazima liwe na nambari') :
      !/[^A-Za-z0-9]/.test(newPassword) ? t('Password must contain a symbol (e.g. !@#$)', 'Nenosiri lazima liwe na alama (mfano !@#$)') :
      null;
    if (complexityIssue) {
      toast.error(complexityIssue);
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t('Passwords do not match', 'Manenosiri hayalingani'));
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      toast.success(t('Password reset successfully!', 'Nenosiri limebadilishwa!'));
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to reset password', 'Imeshindwa kubadilisha nenosiri'));
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <form onSubmit={handleRequestCode} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="identifier">
                {t('Email or Phone', 'Barua Pepe au Simu')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="identifier"
                  type="text"
                  placeholder={t('e.g. john@email.com or 0712345678', 'mfano john@email.com au 0712345678')}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="h-12 pl-10"
                  data-testid="forgot-identifier-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t(
                  "We'll send a 6-digit code to reset your password",
                  "Tutatuma msimbo wa tarakimu 6 kubadilisha nenosiri lako"
                )}
              </p>
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 rounded-full text-base"
              disabled={loading}
              data-testid="forgot-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('Sending...', 'Inatuma...')}
                </>
              ) : (
                t('Send Reset Code', 'Tuma Msimbo')
              )}
            </Button>
          </form>
        );

      case 2:
        return (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">
                {t('Enter 6-Digit Code', 'Ingiza Msimbo wa Tarakimu 6')}
              </Label>
              <Input
                id="code"
                type="text"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="h-14 text-center text-2xl tracking-[0.5em] font-mono"
                maxLength={6}
                data-testid="forgot-code-input"
              />
              <p className="text-xs text-muted-foreground text-center">
                {t(
                  `Code sent to ${identifier}. Check your messages.`,
                  `Msimbo umetumwa kwa ${identifier}. Angalia ujumbe wako.`
                )}
              </p>
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 rounded-full text-base"
              disabled={loading || code.length !== 6}
              data-testid="forgot-verify-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('Verifying...', 'Inathibitisha...')}
                </>
              ) : (
                t('Verify Code', 'Thibitisha Msimbo')
              )}
            </Button>
            <Button 
              type="button"
              variant="ghost"
              className="w-full"
              onClick={() => setStep(1)}
            >
              {t('Request new code', 'Omba msimbo mpya')}
            </Button>
          </form>
        );

      case 3:
        return (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">
                {t('New Password', 'Nenosiri Jipya')}
              </Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="newPassword"
                  type={showNew ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="h-12 pl-10 pr-12"
                  data-testid="forgot-new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                  aria-label={showNew ? t('Hide password', 'Ficha nenosiri') : t('Show password', 'Onyesha nenosiri')}
                  data-testid="forgot-toggle-new-password-visibility"
                >
                  {showNew ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                {t('Confirm Password', 'Thibitisha Nenosiri')}
              </Label>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="confirmPassword"
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-12 pl-10 pr-12"
                  data-testid="forgot-confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus:outline-none"
                  aria-label={showConfirm ? t('Hide password', 'Ficha nenosiri') : t('Show password', 'Onyesha nenosiri')}
                  data-testid="forgot-toggle-confirm-password-visibility"
                >
                  {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 rounded-full text-base"
              disabled={loading}
              data-testid="forgot-reset-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('Resetting...', 'Inabadilisha...')}
                </>
              ) : (
                t('Reset Password', 'Badilisha Nenosiri')
              )}
            </Button>
          </form>
        );

      case 4:
        return (
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold">
              {t('Password Reset Complete!', 'Nenosiri Limebadilishwa!')}
            </h3>
            <p className="text-muted-foreground">
              {t(
                'You can now login with your new password.',
                'Sasa unaweza kuingia na nenosiri lako jipya.'
              )}
            </p>
            <Button 
              onClick={() => navigate('/login')}
              className="w-full h-12 rounded-full text-base"
              data-testid="forgot-login-btn"
            >
              {t('Go to Login', 'Nenda Kuingia')}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1:
        return t('Forgot Password?', 'Umesahau Nenosiri?');
      case 2:
        return t('Enter Code', 'Ingiza Msimbo');
      case 3:
        return t('Create New Password', 'Tengeneza Nenosiri Jipya');
      case 4:
        return t('Success!', 'Imefanikiwa!');
      default:
        return '';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 1:
        return t(
          "Enter your email or phone and we'll send you a code",
          "Ingiza barua pepe au simu yako na tutakutumia msimbo"
        );
      case 2:
        return t(
          "Enter the 6-digit code we sent you",
          "Ingiza msimbo wa tarakimu 6 tuliyokutumia"
        );
      case 3:
        return t(
          "Choose a strong password for your account",
          "Chagua nenosiri imara kwa akaunti yako"
        );
      case 4:
        return '';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Image (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 bg-primary/90" />
        <img 
          src="https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=800"
          alt="Security illustration"
          className="absolute inset-0 w-full h-full object-cover mix-blend-overlay"
        />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6" />
            </div>
            <span className="font-bold text-2xl">Wekeza-Smart</span>
          </div>
          <h1 className="text-4xl font-bold mb-4">
            {t('Reset Your Password', 'Badilisha Nenosiri Lako')}
          </h1>
          <p className="text-white/80 text-lg max-w-md">
            {t(
              "Don't worry, it happens. We'll help you recover access to your account in just a few steps.",
              "Usijali, inaweza kutokea. Tutakusaidia kupata tena akaunti yako kwa hatua chache."
            )}
          </p>
          
          {/* Progress indicator */}
          <div className="mt-8 flex items-center gap-2">
            {[1, 2, 3, 4].map((s) => (
              <div 
                key={s}
                className={`h-2 rounded-full transition-all duration-300 ${
                  s === step 
                    ? 'w-8 bg-white' 
                    : s < step 
                      ? 'w-8 bg-white/60' 
                      : 'w-2 bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col">
        <header className="p-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => step === 1 ? navigate('/login') : setStep(step - 1)}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('Back', 'Rudi')}
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={toggleLanguage}
            className="flex items-center gap-2"
          >
            <Globe className="w-4 h-4" />
            {language === 'en' ? 'Kiswahili' : 'English'}
          </Button>
        </header>

        <div className="flex-1 flex items-center justify-center p-6">
          <Card className="w-full max-w-md border-0 shadow-none lg:border lg:shadow-sm">
            <CardHeader className="text-center lg:text-left">
              <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="font-bold text-xl">Wekeza-Smart</span>
              </div>
              
              {/* Mobile progress indicator */}
              <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
                {[1, 2, 3, 4].map((s) => (
                  <div 
                    key={s}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      s === step 
                        ? 'w-6 bg-primary' 
                        : s < step 
                          ? 'w-6 bg-primary/60' 
                          : 'w-1.5 bg-muted'
                    }`}
                  />
                ))}
              </div>
              
              <CardTitle className="text-2xl">
                {getStepTitle()}
              </CardTitle>
              {getStepDescription() && (
                <CardDescription>
                  {getStepDescription()}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {renderStep()}
              
              {step === 1 && (
                <div className="mt-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    {t("Remember your password?", "Unakumbuka nenosiri lako?")}{' '}
                    <Link 
                      to="/login" 
                      className="text-primary font-medium hover:underline"
                    >
                      {t('Sign in', 'Ingia')}
                    </Link>
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
